import React, { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { 
  FiCalendar, 
  FiMapPin, 
  FiUsers, 
  FiArrowLeft, 
  FiTag, 
  FiDollarSign, 
  FiEye, 
  FiExternalLink,
  FiFileText,
  FiSliders,
  FiCamera,
  FiEdit3,
  FiCheck,
  FiUploadCloud,
  FiImage
} from 'react-icons/fi';
import EventService from '../services/EventService';
import instance from '../services/AxiosServices';

const ManageEvent = () => {
  const { id } = useParams();
  const history = useHistory();

  const [event, setEvent] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPoster, setUploadingPoster] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'edit' | 'tickets' | 'settings'

  // Editable Form Data
  const [formData, setFormData] = useState({
    title: '',
    eventType: 'IRL',
    startDate: '',
    endDate: '',
    startTime: '',
    venueName: '',
    venueAddress: '',
    city: '',
    state: '',
    ticketPrice: 0,
    about: '',
    posterUrl: '',
    bannerUrl: '',
    ticketTypes: [],
  });

  useEffect(() => {
    fetchEventDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchEventDetails = async () => {
    setLoading(true);
    try {
      const [detailRes, analyticsRes] = await Promise.all([
        EventService.getEventById(id).catch(() => null),
        EventService.getEventAnalytics(id).catch(() => null)
      ]);

      if (detailRes) {
        const ev = detailRes.event || detailRes.eventDetails || detailRes;
        setEvent(ev);

        // Populate Edit Form Data
        setFormData({
          title: ev.title || ev.basic?.title || ev.name || '',
          eventType: ev.eventType || ev.type || 'IRL',
          startDate: ev.startDate ? String(ev.startDate).split('T')[0] : (ev.schedule?.startAt ? String(ev.schedule.startAt).split('T')[0] : ''),
          endDate: ev.expiryDate ? String(ev.expiryDate).split('T')[0] : (ev.endDate ? String(ev.endDate).split('T')[0] : ''),
          startTime: ev.startTime || ev.schedule?.startTime || '10:00 AM',
          venueName: ev.venueName || ev.location?.name || '',
          venueAddress: ev.venueAddress || ev.location?.address || '',
          city: ev.city || ev.location?.city || '',
          state: ev.state || ev.location?.state || '',
          ticketPrice: Number(ev.ticketPrice || ev.entrySettings?.feeAmount || ev.price || 0),
          about: ev.about || ev.description || '',
          posterUrl: ev.posterUrl || ev.poster?.url || (typeof ev.poster === 'string' ? ev.poster : ''),
          bannerUrl: ev.bannerUrl || ev.banner?.url || (typeof ev.banner === 'string' ? ev.banner : ''),
          ticketTypes: ev.ticketTiers || ev.ticketTypes || ev.tiers || [
            { name: 'General Admission', price: Number(ev.ticketPrice || 0) }
          ],
        });
      }
      if (analyticsRes) {
        setAnalytics(analyticsRes);
      }
    } catch (err) {
      console.error('Error fetching event details for management:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return 'Date TBA';
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return String(dateVal).split('T')[0];
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
    } catch {
      return String(dateVal).split('T')[0];
    }
  };

  const handleFileUpload = async (file, targetType) => {
    if (!file) return;
    const data = new FormData();
    data.append('media', file);

    if (targetType === 'poster') setUploadingPoster(true);
    if (targetType === 'banner') setUploadingBanner(true);

    try {
      const res = await instance.post('/media/partner/upload', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadedUrl = res?.url || res?.data?.url || res?.mediaUrl || res?.path;

      if (uploadedUrl) {
        if (targetType === 'poster') {
          setFormData(prev => ({ ...prev, posterUrl: uploadedUrl }));
        } else if (targetType === 'banner') {
          setFormData(prev => ({ ...prev, bannerUrl: uploadedUrl }));
        }
        alert('Image uploaded successfully!');
      }
    } catch (err) {
      console.error('Upload error:', err);
      alert('Failed to upload image.');
    } finally {
      if (targetType === 'poster') setUploadingPoster(false);
      if (targetType === 'banner') setUploadingBanner(false);
    }
  };

  const handleSaveEvent = async (e) => {
    if (e) e.preventDefault();
    if (!formData.title) {
      alert('Please enter event name.');
      return;
    }

    setSaving(true);
    try {
      const updatePayload = {
        title: formData.title,
        eventType: formData.eventType,
        startDate: formData.startDate,
        endDate: formData.endDate,
        expiryDate: formData.endDate,
        startTime: formData.startTime,
        venueName: formData.venueName,
        venueAddress: formData.venueAddress,
        city: formData.city,
        state: formData.state,
        ticketPrice: Number(formData.ticketPrice) || 0,
        price: Number(formData.ticketPrice) || 0,
        about: formData.about,
        description: formData.about,
        posterUrl: formData.posterUrl,
        bannerUrl: formData.bannerUrl,
        poster: { url: formData.posterUrl },
        banner: { url: formData.bannerUrl },
        ticketTypes: formData.ticketTypes,
      };

      await EventService.updateEvent(id, updatePayload);
      alert('Event Details Updated Successfully!');
      await fetchEventDetails();
      setActiveTab('overview');
    } catch (err) {
      console.error('Error updating event:', err);
      const errMsg = err.response?.data?.message || err.message || 'Failed to update event.';
      alert(`Error updating event: ${errMsg}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-6 text-left py-12">
        <div className="bg-white border border-slate-200 p-8 rounded-2xl text-center text-slate-500 text-xs font-bold">
          Loading event management dashboard...
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="max-w-5xl mx-auto space-y-6 text-left py-12">
        <div className="bg-white border border-slate-200 p-8 rounded-2xl text-center space-y-4">
          <h2 className="text-lg font-bold text-slate-800">Event Not Found</h2>
          <p className="text-xs text-slate-500">The requested event could not be found or you don't have permission to manage it.</p>
          <button
            onClick={() => history.push('/app/events')}
            className="px-4 py-2 rounded-xl bg-purple-700 text-white text-xs font-bold"
          >
            Back to Events List
          </button>
        </div>
      </div>
    );
  }

  const title = event.title || event.basic?.title || event.name || 'Untitled Event';
  const eventType = event.eventType || event.type || 'IRL';
  const venue = event.venueName || event.location?.name || event.venueAddress || event.location?.address || 'Venue Details Provided';
  const startDateStr = formatDate(event.startDate || event.schedule?.startAt || event.startAt);
  const endDateStr = formatDate(event.expiryDate || event.endDate || event.schedule?.endAt || event.endAt);
  const poster = event.poster?.url || (typeof event.poster === 'string' ? event.poster : null) || event.banner?.url || (typeof event.banner === 'string' ? event.banner : null);
  const banner = event.banner?.url || (typeof event.banner === 'string' ? event.banner : null);
  const ticketPrice = Number(event.ticketPrice || event.entrySettings?.feeAmount || event.feeAmount || 0);
  const isPaid = event.entrySettings?.isPaid || ticketPrice > 0;
  const goingCount = event.interestedCount || analytics?.totalJoinedPeople || event.goingCount || 0;
  const ticketTiers = event.ticketTiers || event.ticketTypes || event.tiers || [];
  const tierCapacitySum = Array.isArray(ticketTiers) && ticketTiers.length > 0
    ? ticketTiers.reduce((acc, t) => acc + Number(t.quantity || t.capacity || t.maxCapacity || 0), 0)
    : 0;
  const maxCapacity = tierCapacitySum > 0 ? tierCapacitySum : (event.maxCapacity || event.capacity || 200);
  const totalViews = analytics?.funnel?.views || event.views || 0;

  // Chart Metrics Calculations
  const remainingSeats = Math.max(0, maxCapacity - goingCount);
  const occupancyPercent = maxCapacity > 0 ? Math.min(100, Math.round((goingCount / maxCapacity) * 1000) / 10) : 0;

  const targetRevenue = (ticketTiers && ticketTiers.length > 0)
    ? ticketTiers.reduce((acc, t) => acc + (Number(t.price || 0) * Number(t.quantity || t.capacity || 0)), 0)
    : (maxCapacity * ticketPrice);

  const calculateTierEarned = (tiers) => {
    if (!Array.isArray(tiers) || tiers.length === 0) return 0;
    return tiers.reduce((acc, t) => {
      const price = Number(t.price || 0);
      const totalQty = Number(t.quantity || t.capacity || 0);
      const avail = Number(t.availableSeats ?? t.seatsAvailable ?? t.availableQuantity ?? totalQty);
      const sold = Math.max(0, totalQty - avail);
      return acc + (price * sold);
    }, 0);
  };

  const actualEarned = (analytics?.totalRevenue !== undefined && analytics?.totalRevenue !== null)
    ? Number(analytics.totalRevenue)
    : calculateTierEarned(ticketTiers);

  const earningPercent = targetRevenue > 0
    ? Math.min(100, Math.round((actualEarned / targetRevenue) * 1000) / 10)
    : 100;

  const calculateDaysLeft = (dateVal) => {
    if (!dateVal) return null;
    const target = new Date(dateVal);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    target.setHours(0, 0, 0, 0);
    if (isNaN(target.getTime())) return null;
    const diffTime = target - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };
  const daysLeft = calculateDaysLeft(event.startDate || event.schedule?.startAt);

  return (
    <div className="max-w-5xl mx-auto space-y-6 text-left pb-24">
      {/* Top Bar with Back Button */}
      <div className="flex items-center justify-between gap-4">
        <button
          onClick={() => history.push('/app/events')}
          className="p-2.5 rounded-xl bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 text-xs font-bold transition-all flex items-center gap-2 shadow-xs"
        >
          <FiArrowLeft className="w-4 h-4" />
          <span>Back to Events</span>
        </button>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveTab('edit')}
            className="px-3.5 py-2 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold transition-all flex items-center gap-1.5 shadow-sm"
          >
            <FiEdit3 className="w-3.5 h-3.5" />
            <span>Edit Event Details</span>
          </button>
          <a
            href={`https://tapcoe.com/events/${event.slug || event._id}`}
            target="_blank"
            rel="noreferrer"
            className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-slate-700 hover:text-purple-700 text-xs font-bold transition-all flex items-center gap-1.5 shadow-xs"
          >
            <span>Public Page</span>
            <FiExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
      </div>

      {/* Hero Banner Card */}
      <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
        {banner ? (
          <div className="h-48 md:h-64 w-full relative bg-slate-900 overflow-hidden">
            <img src={banner} alt={title} className="w-full h-full object-cover opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-transparent"></div>
          </div>
        ) : (
          <div className="h-32 w-full bg-gradient-to-r from-purple-900 via-indigo-900 to-slate-900"></div>
        )}

        <div className="p-6 md:p-8 space-y-4 relative">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex gap-4 items-start">
              {poster && (
                <div className="w-20 h-24 rounded-2xl overflow-hidden border-2 border-white bg-slate-100 shadow-md shrink-0 -mt-12 relative z-10">
                  <img src={poster} alt="" className="w-full h-full object-cover" />
                </div>
              )}

              <div>
                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                  <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold border ${
                    eventType === 'IRL' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : eventType === 'Hybrid' ? 'bg-purple-50 text-purple-800 border-purple-200' : 'bg-blue-50 text-blue-800 border-blue-200'
                  }`}>
                    {eventType === 'IRL' ? '📍 IRL / Offline' : eventType === 'Hybrid' ? '👥 Hybrid' : '🌐 Online'}
                  </span>
                  <span className="px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-600 text-white border border-emerald-500">
                    ● {event.status || 'LIVE'}
                  </span>
                </div>

                <h1 className="text-2xl font-black text-slate-900 tracking-tight leading-snug">{title}</h1>
                <p className="text-xs font-semibold text-purple-900 mt-1">
                  {isPaid ? `Paid Event (Starting ₹${ticketPrice})` : 'Free Entry Event'}
                </p>
              </div>
            </div>
          </div>

          {/* Key Quick Info Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-4 border-t border-slate-100">
            <div className="p-3 rounded-2xl bg-slate-50 border border-slate-100">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">📅 Date</span>
              <span className="text-xs font-extrabold text-slate-800">{startDateStr}</span>
            </div>

            <div className="p-3 rounded-2xl bg-slate-50 border border-slate-100">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">📍 Location</span>
              <span className="text-xs font-extrabold text-slate-800 truncate block">{venue}</span>
            </div>

            <div className="p-3 rounded-2xl bg-slate-50 border border-slate-100">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">👥 Registered</span>
              <span className="text-xs font-extrabold text-purple-900">{goingCount} Attendees</span>
            </div>

            <div className="p-3 rounded-2xl bg-slate-50 border border-slate-100">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">👁️ Views</span>
              <span className="text-xs font-extrabold text-slate-800">{totalViews}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 📊 2 VISUAL PERFORMANCE CHARTS: CAPACITY VS BOOKINGS & EARNINGS VS COUNTDOWN */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* CHART 1: SEAT CAPACITY VS BOOKINGS */}
        <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-purple-600"></span>
                📊 Seat Capacity vs Bookings
              </h3>
              <p className="text-[11px] text-slate-500 font-medium">Live occupancy tracking</p>
            </div>
            <span className="px-2.5 py-1 rounded-full bg-purple-50 text-purple-800 text-[10px] font-extrabold border border-purple-200">
              {occupancyPercent}% Occupied
            </span>
          </div>

          <div className="flex items-center gap-6">
            {/* SVG Donut Gauge Chart */}
            <div className="relative w-28 h-28 flex-shrink-0 flex items-center justify-center">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                <path
                  className="text-slate-100"
                  strokeWidth="3.8"
                  stroke="currentColor"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="text-purple-600 transition-all duration-1000 ease-out"
                  strokeDasharray={`${occupancyPercent}, 100`}
                  strokeWidth="3.8"
                  strokeLinecap="round"
                  stroke="currentColor"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
              <div className="absolute flex flex-col items-center justify-center text-center">
                <span className="text-sm font-black text-slate-900">{goingCount}</span>
                <span className="text-[9px] font-bold text-slate-400 uppercase">Booked</span>
              </div>
            </div>

            {/* Metrics Details */}
            <div className="flex-1 space-y-2.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 font-semibold flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-purple-600"></span> Booked / Registered:
                </span>
                <span className="font-extrabold text-slate-900">{goingCount} Seats</span>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 font-semibold flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-200"></span> Remaining Available:
                </span>
                <span className="font-extrabold text-slate-700">{remainingSeats} Seats</span>
              </div>

              <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-100">
                <span className="text-slate-500 font-semibold">Total Capacity:</span>
                <span className="font-black text-purple-900">{maxCapacity} Seats</span>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="space-y-1 pt-1">
            <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
              <div
                className="bg-gradient-to-r from-purple-700 to-indigo-600 h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.max(3, occupancyPercent)}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-[10px] font-bold text-slate-400">
              <span>0 Seats</span>
              <span>{occupancyPercent}% Booked</span>
              <span>{maxCapacity} Max Capacity</span>
            </div>
          </div>
        </div>

        {/* CHART 2: EARNINGS & EVENT DAYS COUNTDOWN */}
        <div className="bg-white border border-slate-200 p-6 rounded-3xl shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div>
              <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                💰 Earnings & Event Countdown
              </h3>
              <p className="text-[11px] text-slate-500 font-medium">Revenue goal & timeline remaining</p>
            </div>

            {daysLeft !== null && (
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold border ${
                daysLeft > 0 ? 'bg-amber-50 text-amber-900 border-amber-200' : (daysLeft === 0 ? 'bg-emerald-100 text-emerald-900 border-emerald-300' : 'bg-slate-100 text-slate-600')
              }`}>
                {daysLeft > 0 ? `⏳ ${daysLeft} Days Left` : (daysLeft === 0 ? '🔴 Event Today!' : '🏁 Event Completed')}
              </span>
            )}
          </div>

          <div className="flex items-center gap-6">
            {/* SVG Donut Gauge Chart */}
            <div className="relative w-28 h-28 flex-shrink-0 flex items-center justify-center">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                <path
                  className="text-slate-100"
                  strokeWidth="3.8"
                  stroke="currentColor"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
                <path
                  className="text-emerald-500 transition-all duration-1000 ease-out"
                  strokeDasharray={`${earningPercent}, 100`}
                  strokeWidth="3.8"
                  strokeLinecap="round"
                  stroke="currentColor"
                  fill="none"
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                />
              </svg>
              <div className="absolute flex flex-col items-center justify-center text-center p-1">
                <span className="text-xs font-black text-emerald-800">
                  {actualEarned === 0 ? 'FREE' : `₹${actualEarned.toLocaleString('en-IN')}`}
                </span>
                <span className="text-[9px] font-bold text-slate-400 uppercase">Earned</span>
              </div>
            </div>

            {/* Metrics Details */}
            <div className="flex-1 space-y-2.5">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 font-semibold flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> Total Earned So Far:
                </span>
                <span className="font-extrabold text-emerald-700">
                  {actualEarned === 0 ? 'Free Entry' : `₹${actualEarned.toLocaleString('en-IN')}`}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500 font-semibold flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-slate-200"></span> Revenue Potential:
                </span>
                <span className="font-extrabold text-slate-800">
                  {targetRevenue === 0 ? 'Free Event' : `₹${targetRevenue.toLocaleString('en-IN')}`}
                </span>
              </div>

              <div className="flex items-center justify-between text-xs pt-1 border-t border-slate-100">
                <span className="text-slate-500 font-semibold">Event Start Date:</span>
                <span className="font-extrabold text-slate-900">{startDateStr}</span>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="space-y-1 pt-1">
            <div className="w-full bg-slate-100 h-2.5 rounded-full overflow-hidden">
              <div
                className="bg-gradient-to-r from-emerald-500 to-teal-600 h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.max(3, earningPercent)}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-[10px] font-bold text-slate-400">
              <span>₹0</span>
              <span>{earningPercent}% Revenue Achieved</span>
              <span>{targetRevenue > 0 ? `₹${targetRevenue.toLocaleString('en-IN')}` : 'Free'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        {[
          { key: 'overview', icon: FiFileText, label: 'Overview & Details' },
          { key: 'edit', icon: FiEdit3, label: '✏️ Edit Event Details' },
          { key: 'tickets', icon: FiTag, label: `Ticket Passes (${ticketTiers.length})` },
          { key: 'settings', icon: FiSliders, label: 'Event Controls' },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 ${
              activeTab === t.key
                ? 'bg-purple-700 text-white shadow-sm'
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
            }`}
          >
            <t.icon className="w-4 h-4" />
            <span>{t.label}</span>
          </button>
        ))}
      </div>

      {/* TAB 1: OVERVIEW & DETAILS */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 p-6 md:p-8 rounded-2xl space-y-6 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                <FiFileText className="text-purple-700" /> Event Details & Schedule
              </h3>
              <button
                onClick={() => setActiveTab('edit')}
                className="text-xs font-bold text-purple-700 hover:underline flex items-center gap-1"
              >
                <FiEdit3 className="w-3.5 h-3.5" /> Edit Event
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">📅 Date & Time</span>
                  <span className="text-sm font-bold text-slate-900 block">{startDateStr} {endDateStr !== startDateStr ? ` to ${endDateStr}` : ''}</span>
                  <span className="text-xs text-slate-500 font-medium block">⏰ {event.startTime || 'Start Time TBA'}</span>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">📍 Venue & Address</span>
                  <span className="text-sm font-bold text-slate-900 block">{venue}</span>
                  {event.venueAddress && <span className="text-xs text-slate-500 font-medium block">{event.venueAddress}</span>}
                </div>
              </div>

              <div className="space-y-4">
                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">🏷️ Category & Format</span>
                  <span className="text-sm font-bold text-slate-900 block">{event.category?.name || event.type || 'Event'} ({eventType})</span>
                </div>

                <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-1">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">💰 Entry Pricing</span>
                  <span className="text-sm font-extrabold text-purple-900 block">{isPaid ? `₹${ticketPrice} per ticket` : 'Free Entry (No Ticket Fee)'}</span>
                </div>
              </div>
            </div>

            {/* About / Description */}
            <div className="pt-4 border-t border-slate-100 space-y-2">
              <span className="text-xs font-bold text-slate-900 block">About Event Description</span>
              <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-line font-medium bg-slate-50 p-4 rounded-2xl border border-slate-100">
                {event.about || event.description || 'No description provided for this event.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: EDIT EVENT FORM */}
      {activeTab === 'edit' && (
        <form onSubmit={handleSaveEvent} className="bg-white border border-slate-200 p-6 md:p-8 rounded-2xl space-y-6 shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-200 pb-3">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <FiEdit3 className="text-purple-700" /> Edit Event Information & Pricing
            </h3>
            <span className="text-xs text-slate-500 font-medium">Update details live</span>
          </div>

          <div className="space-y-4">
            {/* Title */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Event Name / Title *</label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
              />
            </div>

            {/* Event Format */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Event Format</label>
                <select
                  value={formData.eventType}
                  onChange={(e) => setFormData({ ...formData, eventType: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                >
                  <option value="IRL">📍 IRL / Offline Venue Event</option>
                  <option value="Hybrid">👥 Hybrid Event (Offline + Online)</option>
                  <option value="Online">🌐 Online Stream Session</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">General Entry Ticket Price (₹)</label>
                <input
                  type="number"
                  value={formData.ticketPrice}
                  onChange={(e) => setFormData({ ...formData, ticketPrice: e.target.value })}
                  placeholder="0 for Free Entry"
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-bold"
                />
              </div>
            </div>

            {/* Start & End Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">End / Expiry Date</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Start Time</label>
                <input
                  type="text"
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  placeholder="e.g. 10:00 AM"
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-semibold"
                />
              </div>
            </div>

            {/* Venue & Location */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Venue Name</label>
                <input
                  type="text"
                  value={formData.venueName}
                  onChange={(e) => setFormData({ ...formData, venueName: e.target.value })}
                  placeholder="e.g. Bikaner Cultural Auditorium"
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">Full Venue Address</label>
                <input
                  type="text"
                  value={formData.venueAddress}
                  onChange={(e) => setFormData({ ...formData, venueAddress: e.target.value })}
                  placeholder="e.g. Main Ring Road, Near City Plaza"
                  className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                />
              </div>
            </div>

            {/* Poster & Banner Upload */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                <label className="block text-xs font-bold text-slate-700">Poster Card Image URL</label>
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    value={formData.posterUrl}
                    onChange={(e) => setFormData({ ...formData, posterUrl: e.target.value })}
                    placeholder="https://..."
                    className="flex-1 bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                  />
                  <label className="px-3 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold cursor-pointer transition-all flex items-center gap-1 shrink-0 shadow-sm">
                    <FiUploadCloud />
                    <span>{uploadingPoster ? 'Uploading...' : 'Upload'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploadingPoster}
                      onChange={(e) => handleFileUpload(e.target.files[0], 'poster')}
                    />
                  </label>
                </div>
                {formData.posterUrl && (
                  <div className="w-20 h-24 rounded-lg overflow-hidden border border-purple-200">
                    <img src={formData.posterUrl} alt="Poster" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3">
                <label className="block text-xs font-bold text-slate-700">Banner Cover Image URL</label>
                <div className="flex items-center gap-2">
                  <input
                    type="url"
                    value={formData.bannerUrl}
                    onChange={(e) => setFormData({ ...formData, bannerUrl: e.target.value })}
                    placeholder="https://..."
                    className="flex-1 bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
                  />
                  <label className="px-3 py-2.5 rounded-xl bg-indigo-700 hover:bg-indigo-800 text-white text-xs font-bold cursor-pointer transition-all flex items-center gap-1 shrink-0 shadow-sm">
                    <FiUploadCloud />
                    <span>{uploadingBanner ? 'Uploading...' : 'Upload'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={uploadingBanner}
                      onChange={(e) => handleFileUpload(e.target.files[0], 'banner')}
                    />
                  </label>
                </div>
                {formData.bannerUrl && (
                  <div className="w-32 h-18 rounded-lg overflow-hidden border border-indigo-200">
                    <img src={formData.bannerUrl} alt="Banner" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">About Event Description</label>
              <textarea
                rows={5}
                value={formData.about}
                onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                placeholder="Explain what attendees can expect, guidelines, schedule..."
                className="w-full bg-white border border-slate-300 rounded-xl p-3 text-xs text-slate-900 outline-none focus:border-purple-600 font-medium"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={() => setActiveTab('overview')}
              className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 text-xs font-bold transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-7 py-2.5 rounded-xl bg-purple-700 hover:bg-purple-800 text-white text-xs font-bold transition-all shadow-sm flex items-center gap-2"
            >
              <FiCheck className="w-4 h-4" />
              <span>{saving ? 'Saving Changes...' : 'Save Event Changes'}</span>
            </button>
          </div>
        </form>
      )}

      {/* TAB 3: TICKETS & PASSES */}
      {activeTab === 'tickets' && (
        <div className="bg-white border border-slate-200 p-6 md:p-8 rounded-2xl space-y-6 shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <FiTag className="text-purple-700" /> Ticket Tiers & Passes Configured
            </h3>
            <Link
              to="/app/attendees"
              className="text-xs font-bold text-purple-700 hover:underline"
            >
              View All Attendees &gt;
            </Link>
          </div>

          {ticketTiers.length === 0 ? (
            <div className="p-6 rounded-2xl bg-slate-50 border border-slate-200 text-center space-y-2">
              <span className="text-sm font-bold text-slate-800 block">Standard Pass Entry</span>
              <span className="text-xs text-slate-500 font-medium block">{isPaid ? `General Entry Pass • ₹${ticketPrice}` : 'General Free Entry Pass'}</span>
            </div>
          ) : (
            <div className="space-y-3">
              {ticketTiers.map((t, idx) => (
                <div key={idx} className="p-4 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-between">
                  <div>
                    <h4 className="text-xs font-bold text-slate-900">{t.name || t.title || 'General Entry Pass'}</h4>
                    <span className="text-[11px] text-slate-500 font-medium">{t.description || 'Valid event pass'}</span>
                  </div>
                  <span className="text-sm font-black text-purple-900">
                    {Number(t.price || 0) === 0 ? 'FREE' : `₹${t.price}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB 4: CONTROLS & SETTINGS */}
      {activeTab === 'settings' && (
        <div className="bg-white border border-slate-200 p-6 md:p-8 rounded-2xl space-y-6 shadow-sm">
          <h3 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-3 flex items-center gap-2">
            <FiSliders className="text-purple-700" /> Event Status & Quick Actions
          </h3>

          <div className="space-y-4">
            <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-slate-900 block">Event Active Status</span>
                <span className="text-[11px] text-slate-500 font-medium">Current status is <strong className="text-purple-700">{event.status || 'LIVE'}</strong></span>
              </div>

              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full text-xs font-extrabold bg-emerald-100 text-emerald-800">
                  ● {event.status || 'LIVE'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageEvent;
